import type { ReactElement } from "react";

export function ModelsAndParameters(): ReactElement {
  return (
    <>
      <h2>Where the controls come from</h2>
      <p>
        Darkroom does not hand-write a settings form per model. The server publishes a description
        of every model — what it can do and what parameters it takes, each with a kind, a range, a
        default and a group — and this interface builds the controls from that description.
      </p>
      <p>
        The practical result: when a provider adds a parameter, it appears here as soon as the
        server's catalog lists it. Nothing in the interface has to know about it in advance.
      </p>

      <h2>How a control is chosen</h2>
      <table className="table">
        <thead>
          <tr>
            <th>Parameter</th>
            <th>Control</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>A choice whose options carry aspect ratios</td>
            <td>Swatches drawn in the true proportion</td>
          </tr>
          <tr>
            <td>A short choice with brief labels</td>
            <td>Segmented buttons</td>
          </tr>
          <tr>
            <td>Any longer choice</td>
            <td>Dropdown</td>
          </tr>
          <tr>
            <td>A number with both a minimum and a maximum</td>
            <td>Slider with a numeric field</td>
          </tr>
          <tr>
            <td>Any other number</td>
            <td>Numeric field</td>
          </tr>
          <tr>
            <td>A yes/no</td>
            <td>Switch</td>
          </tr>
          <tr>
            <td>A seed</td>
            <td>Numeric field, randomise, and a lock</td>
          </tr>
          <tr>
            <td>Free text</td>
            <td>Text field</td>
          </tr>
        </tbody>
      </table>

      <h2>Groups, advanced and dependants</h2>
      <p>
        Parameters are grouped as output, quality, content and advanced. Anything the catalog marks
        as advanced sits behind a disclosure, whichever group it belongs to.
      </p>
      <p>
        Some parameters only make sense alongside a particular value of another one. Those appear
        and disappear as you change the parameter they depend on, and a hidden value is never sent.
      </p>

      <h2>The experimental badge</h2>
      <p>
        An <strong>experimental</strong> badge means the parameter is documented somewhere but could
        not be confirmed against the provider's official reference. It may be ignored, or the
        provider may reject the request. Everything without the badge was checked.
      </p>

      <h2>Defaults, and what gets sent</h2>
      <p>
        A parameter you have not touched is sent at its declared default. Where the catalog declares
        no default, the parameter is left out of the request entirely and the provider applies its
        own — which is not always the same thing, so the distinction is kept.
      </p>

      <h2>Switching model</h2>
      <p>
        Parameter names mean the same thing across providers where they can:{" "}
        <code>size</code>, <code>aspect_ratio</code>, <code>quality</code> and <code>seed</code> are
        Darkroom's names, mapped to each provider's own on the way out. So switching model keeps the
        settings the new model also understands, and quietly drops the ones it does not. Settings
        are remembered per model, so switching away and back restores what you had.
      </p>

      <h2>When the server disagrees</h2>
      <p>
        The server validates parameters again before spending anything, and a rejection comes back
        attached to the control that caused it rather than as one message at the top.
      </p>
    </>
  );
}
